// src/pages/PrivacyPolicyPage.jsx

import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

import { api } from "../api/apiClient";

import {
  ThaiPaperBackground,
  Section,
  OrnamentalDivider,
} from "../components/layout/Sections";

import SiteHeader from "../components/SiteHeader";
import ScrollToTopButton from "../components/ScrollToTopButton";

const SITE_URL = "https://the-mea-thai-cuisine.com";
const BUSINESS_NAME = "The Mea Thai Cuisine";
const APP_NAME = "The Mea Thai Cuisine";
const LAST_UPDATED = "October 14, 2026";

const DEFAULT_ORDER_LINK =
  "https://polite-mud-02f9f1a0f.6.azurestaticapps.net";

const privacySections = [
  {
    id: "information-collected",
    title: "1. Information We Collect",
    paragraphs: [
      "When you use our website, mobile application, or online ordering services, we may collect information you voluntarily provide to us.",
      "Depending on the features you use, this may include:",
    ],
    bullets: [
      "Personal information, such as your name, email address, and telephone number.",
      "Order information, including selected menu items, special instructions, order totals, and order history.",
      "Pickup or delivery information, including your delivery address when applicable.",
      "Account information, saved preferences, and information used to provide convenient reordering features.",
      "Messages, customer feedback, and other information you choose to submit.",
      "Technical information, such as device type, app version, IP address, and diagnostic information, where collected by our systems or service providers.",
    ],
    closing:
      "The information collected depends on the services and features you use. We do not require you to provide information unrelated to the service you are requesting.",
  },
  {
    id: "information-use",
    title: "2. How We Use Your Information",
    paragraphs: [
      "We use customer information to operate our restaurant, provide online ordering services, and improve the customer experience.",
    ],
    bullets: [
      "Process, prepare, and fulfill food orders.",
      "Contact you regarding your order, pickup, delivery, or customer service requests.",
      "Process payments through applicable payment processing providers.",
      "Provide account-related features, saved preferences, and convenient reordering.",
      "Maintain records of transactions and resolve billing or order issues.",
      "Respond to customer questions, requests, and feedback.",
      "Maintain the security, reliability, and functionality of our services.",
      "Comply with applicable legal, tax, accounting, and regulatory obligations.",
    ],
  },
  {
    id: "payment-information",
    title: "3. Payment Information",
    paragraphs: [
      "If you make a purchase through our online ordering services, payment information may be handled by the payment processing provider used for that transaction.",
      "Payment processors may collect payment card information and other transaction details needed to authorize and complete purchases.",
      "The handling of payment credentials by a payment provider is subject to that provider's applicable privacy and security practices. Transaction information necessary for order fulfillment, refunds, reconciliation, and recordkeeping may be available to our business.",
    ],
  },
  {
    id: "sharing",
    title: "4. Information Sharing",
    paragraphs: [
      "We do not sell customer personal information.",
      "We may disclose information when reasonably necessary to provide our services, including to:",
    ],
    bullets: [
      "Payment processing providers that handle transactions.",
      "Technology, website hosting, cloud storage, and application infrastructure providers.",
      "Ordering, notification, and communication service providers that support our services.",
      "Delivery providers or other fulfillment partners, when applicable to your order.",
      "Professional advisers, regulators, or authorities when legally required or reasonably necessary to protect rights and security.",
    ],
    closing:
      "Service providers receive information relevant to the services they perform. Their handling of information may also be governed by their own privacy policies.",
  },
  {
    id: "app-data",
    title: "5. Mobile Application Data",
    paragraphs: [
      "The Mea Thai Cuisine mobile application is intended to make browsing our menu, placing orders, and reordering more convenient.",
      "Depending on available functionality, the application may store or retrieve customer preferences, account information, or previous order details.",
      "Certain information may be stored locally on your device or remotely in systems supporting our application. Uninstalling the application may remove locally stored information, but it does not necessarily delete records maintained on our servers.",
      "Technical information may also be processed to maintain application functionality, diagnose errors, and help protect our systems.",
    ],
  },
  {
    id: "data-security",
    title: "6. Data Security",
    paragraphs: [
      "We use reasonable administrative and technical safeguards designed to protect information against unauthorized access, alteration, disclosure, and loss.",
      "These safeguards may include secure communication protocols, access controls, and security features provided by our hosting and payment service providers.",
      "No electronic storage or transmission system can be guaranteed to be completely secure. We encourage customers to protect their devices and account credentials.",
    ],
  },
  {
    id: "retention",
    title: "7. Data Retention",
    paragraphs: [
      "We retain personal information for as long as reasonably necessary to provide our services, maintain required business records, address disputes, and comply with applicable legal obligations.",
      "Different types of information may have different retention periods. For example, completed transaction records may need to be retained for accounting, tax, fraud prevention, or other legal purposes.",
      "When information is no longer required, we take reasonable steps to delete, anonymize, or otherwise dispose of it securely.",
    ],
  },
  {
    id: "deletion",
    title: "8. Access and Data Deletion Requests",
    paragraphs: [
      "You may contact The Mea Thai Cuisine to request access to, correction of, or deletion of personal information associated with our services.",
      "To submit a request, use the contact information or contact form available on our restaurant website. Please identify your request as a privacy or data deletion request and include enough information for us to locate the relevant records.",
      "We may need to verify that you are authorized to make the request before processing it.",
      "We will review your request and delete eligible information, subject to any records we are required or permitted to retain for legal, tax, accounting, security, or other legitimate reasons.",
      "Deleting the mobile application from your device does not automatically delete customer information maintained in our business systems.",
    ],
  },
  {
    id: "cookies",
    title: "9. Cookies and Similar Technologies",
    paragraphs: [
      "Our website and online ordering services may use cookies, local storage, or similar technologies to maintain sessions, remember preferences, support functionality, and help protect our services.",
      "Certain service providers may also use technologies necessary to operate their services. You may be able to control some website cookies through your browser settings, although disabling them could affect functionality.",
    ],
  },
  {
    id: "children",
    title: "10. Children's Privacy",
    paragraphs: [
      "Our online ordering services are intended for a general restaurant customer audience and are not specifically directed toward children under 13.",
      "We do not knowingly seek to collect personal information directly from children under 13. If you believe a child has provided personal information without appropriate authorization, please contact us so we can review the situation.",
    ],
  },
  {
    id: "external-services",
    title: "11. External Websites and Services",
    paragraphs: [
      "Our website or application may contain links to third-party websites or services, including payment and ordering-related services.",
      "We are not responsible for the privacy practices of independently operated third-party websites. We encourage customers to review the privacy policies of those providers.",
    ],
  },
  {
    id: "changes",
    title: "12. Changes to This Privacy Policy",
    paragraphs: [
      "We may update this Privacy Policy as our technology, business operations, or legal requirements change.",
      "Changes will be reflected on this page with an updated effective date. We encourage you to review this policy periodically.",
    ],
  },
];

function PrivacyPolicyPage() {
  const [orderLink, setOrderLink] = useState(DEFAULT_ORDER_LINK);
  const [isOrderingEnabled, setIsOrderingEnabled] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    let active = true;

    const loadSiteSettings = async () => {
      const [flagResult, linkResult] = await Promise.allSettled([
        api.get(
          "/TCVariable/value/Mea-Online-Ordering-Website-On-Off",
          {
            params: {
              name: "Mea-Online-Ordering-Website-On-Off",
            },
          }
        ),
        api.get("/TCVariable/link"),
      ]);

      if (!active) return;

      if (flagResult.status === "fulfilled") {
        const rawValue = String(
          flagResult.value.data ?? ""
        ).trim().toLowerCase();

        setIsOrderingEnabled(
          !["off", "0", "false"].includes(rawValue)
        );
      }

      if (
        linkResult.status === "fulfilled" &&
        typeof linkResult.value.data === "string" &&
        linkResult.value.data.startsWith("https://")
      ) {
        setOrderLink(linkResult.value.data);
      }
    };

    loadSiteSettings();

    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);

    return () => {
      active = false;
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <ThaiPaperBackground>
      <Helmet>
        <title>
          Privacy Policy | The Mea Thai Cuisine
        </title>

        <meta
          name="description"
          content="Read The Mea Thai Cuisine Privacy Policy for our restaurant website, online ordering platform, and mobile application."
        />

        <link
          rel="canonical"
          href={`${SITE_URL}/privacy-policy`}
        />

        <meta
          property="og:title"
          content="Privacy Policy | The Mea Thai Cuisine"
        />

        <meta
          property="og:description"
          content="Learn how The Mea Thai Cuisine collects, uses, protects, and manages customer information."
        />

        <meta
          property="og:url"
          content={`${SITE_URL}/privacy-policy`}
        />
      </Helmet>

      <SiteHeader
        orderLink={orderLink}
        isOrderingEnabled={isOrderingEnabled}
        mobileNavOpen={mobileNavOpen}
        setMobileNavOpen={setMobileNavOpen}
      />

      {/* HERO */}
      <Section tone="warm">
        <div className="max-w-5xl mx-auto py-10 md:py-16 text-center">
          <div className="inline-flex items-center rounded-full bg-red-50 border border-red-100 px-4 py-2 text-sm font-semibold text-red-700 mb-5">
            Your Privacy Matters
          </div>

          <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-gray-900 mb-5">
            Privacy Policy
          </h1>

          <p className="max-w-3xl mx-auto text-lg md:text-xl leading-relaxed text-gray-700">
            We value the trust you place in The Mea Thai Cuisine.
            This policy explains how we handle information when
            you use our website, online ordering services, and
            mobile application.
          </p>

          <p className="text-sm text-gray-500 mt-5">
            Last Updated: {LAST_UPDATED}
          </p>

          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Link
              to="/"
              className="rounded-full bg-red-600 text-white font-bold px-7 py-3 shadow-sm hover:bg-red-700 transition"
            >
              Back to Restaurant
            </Link>

            <a
              href="#contact-privacy"
              className="rounded-full bg-white border border-gray-300 text-gray-800 font-semibold px-7 py-3 hover:bg-gray-50 transition"
            >
              Privacy Questions
            </a>
          </div>
        </div>
      </Section>

      {/* OVERVIEW */}
      <Section tone="light">
        <div className="max-w-5xl mx-auto">
          <div className="rounded-3xl bg-white/85 backdrop-blur-sm shadow-sm border border-gray-100 p-7 md:p-10">
            <p className="text-sm uppercase tracking-[0.18em] font-semibold text-red-700 mb-3">
              About This Policy
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5">
              Our Commitment to Your Privacy
            </h2>

            <div className="space-y-4 text-gray-700 leading-8">
              <p>
                This Privacy Policy describes how{" "}
                <strong>{BUSINESS_NAME}</strong>, located in
                Plymouth, Massachusetts, handles personal
                information collected through its restaurant
                website, online ordering services, and
                customer-facing mobile application.
              </p>

              <p>
                This policy applies to our mobile application
                known as <strong>{APP_NAME}</strong>, including
                the Android application distributed through
                Google Play.
              </p>

              <p>
                By using our services, you acknowledge the
                information practices described in this policy.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <OrnamentalDivider />

      {/* PRIVACY SECTIONS */}
      <Section tone="warm">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-[240px_minmax(0,1fr)] gap-7 items-start">
          {/* TABLE OF CONTENTS */}
          <nav
            aria-label="Privacy policy sections"
            className="hidden lg:block sticky top-24 rounded-2xl bg-white/85 border border-gray-100 shadow-sm p-5"
          >
            <h2 className="font-bold text-gray-900 mb-4">
              On This Page
            </h2>

            <div className="space-y-3">
              {privacySections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="block text-sm text-gray-600 hover:text-red-700 transition"
                >
                  {section.title}
                </a>
              ))}

              <a
                href="#contact-privacy"
                className="block text-sm font-semibold text-red-700 hover:underline"
              >
                13. Contact Us
              </a>
            </div>
          </nav>

          {/* POLICY */}
          <div className="space-y-5 min-w-0">
            {privacySections.map((section) => (
              <article
                key={section.id}
                id={section.id}
                className="scroll-mt-24 rounded-3xl bg-white/85 backdrop-blur-sm shadow-sm border border-gray-100 p-6 md:p-9"
              >
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-5">
                  {section.title}
                </h2>

                <div className="space-y-4 text-gray-700 leading-8">
                  {section.paragraphs?.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}

                  {section.bullets && (
                    <ul className="list-disc pl-6 space-y-2">
                      {section.bullets.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  )}

                  {section.closing && (
                    <p>{section.closing}</p>
                  )}
                </div>
              </article>
            ))}

            {/* CONTACT */}
            <article
              id="contact-privacy"
              className="scroll-mt-24 rounded-3xl bg-white/90 backdrop-blur-sm shadow-sm border border-gray-100 overflow-hidden"
            >
              <div className="bg-red-700 text-white p-6 md:p-8">
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-red-100 mb-2">
                  We're Here to Help
                </p>

                <h2 className="text-2xl md:text-3xl font-bold">
                  13. Contact Us
                </h2>
              </div>

              <div className="p-6 md:p-9 space-y-5 text-gray-700 leading-8">
                <p>
                  For questions about this Privacy Policy,
                  the information we maintain, or requests
                  concerning your personal data, please
                  contact us through our restaurant website.
                </p>

                <div className="rounded-2xl bg-gray-50 border border-gray-100 p-5">
                  <p className="font-bold text-gray-900 text-lg">
                    The Mea Thai Cuisine
                  </p>
                  <p>Plymouth, Massachusetts, USA</p>
                  <p>
                    Website:{" "}
                    <a
                      href={SITE_URL}
                      className="text-red-700 underline break-words"
                    >
                      {SITE_URL}
                    </a>
                  </p>
                  <p>
                    Application: {APP_NAME}
                  </p>
                </div>

                <p>
                  When submitting a privacy-related request,
                  please describe your concern and include
                  contact or order information sufficient for
                  us to identify the relevant records.
                  Do not include passwords or full payment
                  card numbers.
                </p>

                <Link
                  to="/"
                  state={{ scrollTo: "contact" }}
                  className="inline-flex items-center justify-center rounded-full bg-red-600 text-white font-bold px-7 py-3 shadow-sm hover:bg-red-700 transition"
                >
                  Contact Our Restaurant
                </Link>
              </div>
            </article>
          </div>
        </div>
      </Section>

      {/* FOOTER CTA */}
      <Section tone="light">
        <div className="max-w-4xl mx-auto rounded-3xl bg-white/80 backdrop-blur-sm shadow-sm border border-gray-100 p-7 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            Thank You for Trusting The Mea Thai Cuisine
          </h2>

          <p className="text-gray-700 max-w-2xl mx-auto mb-6">
            We appreciate every customer and strive to make
            your ordering experience convenient, reliable,
            and secure.
          </p>

          <Link
            to="/"
            className="inline-flex rounded-full bg-red-600 text-white font-bold px-7 py-3 hover:bg-red-700 transition"
          >
            Return to Home Page
          </Link>
        </div>
      </Section>

      <ScrollToTopButton show={showScrollTop} />
    </ThaiPaperBackground>
  );
}

export default PrivacyPolicyPage;
